// Keyless Fleet Domain Configuration
export const keylessfleetConfig = {
  // Basic domain information
  id: 'keyless-fleet',
  name: 'Keyless Fleet',
  tagline: 'Keyless Fleet Management',
  description: 'Advanced keyless entry and fleet access control.',
  
  // Assets
  logo: '/assets/logos/keyless-fleet-logo',
  whiteLogo: '/assets/logos/keyless-fleet-white-logo',
  favicon: '/assets/favicons/keyless-fleet-favicon.png',
  icon: '/assets/icons/keyless-fleet',
  
  // Navigation
  pages: ['home', 'solutions', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Learn More'
  },
    
  // Shop link
  shopLink: 'https://shop.fleethoster.com/collections/keyless-system',

  // UI Configuration
  variant: 'clean',
  showFeatures: true,
  showTestimonials: true,
  
  // Content sections
  hero: {
    title: 'Keyless Fleet Management',
    subtitle: 'Advanced keyless entry and fleet access control. Secure, convenient, and efficient.',
    features: ['Keyless Entry', 'Access Control', 'Remote Management']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'Keyless Fleet Access Solutions',
    subtitle: 'Explore our advanced keyless entry and fleet access control systems',
    description: 'See how Keyless Fleet revolutionizes fleet management with secure, convenient access control'
  },

  features: {
    title: 'Keyless Fleet Features',
    subtitle: 'Advanced Keyless Access Control',
    description: 'Revolutionary keyless access control system for modern fleet management and security.',
    items: [
      {
        title: 'Remote access',
        description: 'Configure, control and monitor Keyless hardware remotely with Keyless Monitor. Support your drivers with remote access to lock and unlock vehicles.',
        icon: '🔑'
      },
      {
        title: 'Mobile access',
        description: 'On-demand shared vehicle access through Geotab Drive or a mobile app from a shared mobility solution provider. The phone app allows drivers to locate, lock and unlock vehicles in locations with or without data connectivity.',
        icon: '📱'
      },
      {
        title: 'Tap card access',
        description: 'No phone? Provide your drivers with NFC tap cards to lock and unlock vehicles when mobile phone access is not feasible or permitted.',
        icon: '👆'
      },
      {
        title: 'Audit Trail',
        description: 'Comprehensive audit trail with detailed access logs and security reporting.',
        icon: '📊'
      }
    ]
  },
  
  solutions: {
    title: 'Keyless Fleet Solutions',
    subtitle: 'Advanced keyless entry and fleet access control',
    description: 'Revolutionize your fleet management with keyless entry systems. Secure, convenient, and efficient.',
    sections: [
      {
        title: 'Reduce Cost and improve productivity',
        description: 'Eliminate the hassle and expense of managing physical keys with keyless entry systems. With Geotab Keyless, say goodbye to lost keys and hello to a more efficient fleet. Streamline your fleet operations with a secure, convenient, and cost-effective solution.'
      },
      {
        title: 'Boost Fleet Utilization',
        description: "Maximize the use of your vehicles and increase operational efficiency. Geotab Keyless provides 24/7 self-service access, allowing drivers to pick up and drop off vehicles anytime, anywhere. This increases fleet utilization and reduces idle time."
      },
      {
        title: 'Enhanced Security',
        description: 'Safeguard your vehicles with advanced security features. Geotab Keyless provides real-time alerts and monitoring, ensuring that only authorized drivers can access your vehicles. This reduces the risk of unauthorized use and protects your fleet from theft and vandalism.'
      },
      {
        title: 'Seamless Integrations',
        description: 'Integrate Geotab Keyless with your existing fleet management system for a seamless and efficient fleet experience. Geotab Keyless is compatible with Geotab, allowing you to manage your fleet with a single platform.'
      }
    ]
  },
  
  
  testimonials: [
    {
      content: "I don't know how we'd do it without Geotab – the solution is literally in every aspect of our operations.",
      author: "Jonathan Hamilton",
      role: "Director of Fleet Safety and Compliance",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/25.jpg"
    },
    {
      content: "We're excited about what's coming next. It just keeps getting better and better, working with their technology and continuing to evolve. It's been great.",
      author: "Christian Reisch",
      role: "Co-owner",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/20.jpg"
    }
  ],

  blog: {
    title: 'Keyless Fleet Blog',
    subtitle: 'Latest News and Insights',
    description: 'Stay informed with the latest news and insights in fleet management and industry best practices.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'Keyless Fleet | Advanced Keyless Management & Access Control',
    description: 'Advanced keyless entry and fleet access control systems for secure, convenient, and efficient fleet management.',
    keywords: [
      'keyless fleet', 'fleet access control', 'keyless entry', 'fleet security', 'fleet management',
      'keyless fleet management', 'fleet access systems', 'keyless technology', 'fleet security systems', 'access control',
      'fleet keyless entry', 'vehicle access control', 'fleet security management', 'keyless fleet solutions',
      'fleet access management', 'keyless fleet technology', 'fleet security access', 'keyless fleet systems'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'Keyless Fleet - Advanced Keyless Fleet Management Solutions',
    ogDescription: 'Advanced keyless entry and fleet access control. Secure, convenient, and efficient.',
    ogType: 'website',
    ogUrl: 'https://keylessfleet.com',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'Keyless Fleet - Advanced Keyless Fleet Management Solutions',
    twitterDescription: 'Advanced keyless entry and fleet access control. Secure, convenient, and efficient.',
    
    // Robots
    robots: 'index, follow',
    author: 'Keyless Fleet',
    publisher: 'Keyless Fleet',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'Keyless Fleet - Keyless Management & Access Control Solutions',
        description: 'Advanced keyless entry and fleet access control systems. Secure, convenient, and efficient fleet management.',
        keywords: ['keyless fleet', 'fleet access control', 'keyless entry', 'fleet security', 'fleet management']
      },
      solutions: {
        title: 'Keyless Fleet Solutions - Professional Keyless Services',
        description: 'Comprehensive keyless fleet solutions and access control services. Professional fleet security and keyless management systems.',
        keywords: ['keyless fleet solutions', 'fleet access services', 'keyless management', 'fleet security services']
      },
      products: {
        title: 'Keyless Fleet - Access Control Technology & Equipment',
        description: 'Professional keyless fleet products and access control equipment. Advanced keyless technology and fleet security systems.',
        keywords: ['keyless fleet products', 'access control equipment', 'keyless technology', 'fleet security systems']
      },
      blog: {
        title: 'Keyless Fleet News, Insights & Tips',
        description: 'Latest news, insights, and best practices for keyless fleet management and access control. Professional tips and technology updates.',
        keywords: ['keyless fleet news', 'fleet access insights', 'keyless technology tips', 'fleet security best practices']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
